<script lang="ts">import { onMount } from 'svelte';
import FlyoutToggle from '../../shared/FlyoutToggle/FlyoutToggle.svelte';
import Link from '../../shared/Link/Link.svelte';
import LinkList from '../../shared/LinkList/LinkList.svelte';
import { setCtaSubnavButtonsVisibility } from '../../../../js-v2/stores/desktop-cta-subnav-stores';
import { getCurrentUrl } from '../../../../js-v2/stores/url-stores';
import { getLoggedIn, getCartCount } from '../../../../js-v2/stores/user-state-stores';
import { getComponentType } from '../../../../js-v2/componentUtils';
import { filterNavItems, shouldRenderCta, shouldRenderSecondaryCta } from '../../../../js-v2/navItemFilters';
import { urlsAreEqual } from '../../../../js/urlUtils';
const { id = '', title = '', navItems = [], primaryCtaButton = undefined, secondaryCtaButton = undefined, onlyShowCtaWhenSticky = false, showCtaOnCtaPage = false, hiddenUntilScrolled = false, reverseDesktopCtaButtons = false, isSticky = false, } = $props();
const cartCountStore = getCartCount();
const currentUrlStore = getCurrentUrl();
const loggedInStore = getLoggedIn();
// desktopNavHeight is bound to header height below
// and used to size the shim element
let desktopNavHeight = $state();
// desktopNavWidth is bound to nav width below
// and used to set the width of the header
// (which it needs when it's position fixed)
let desktopNavWidth = $state();
let ctaSubnavDesktopHeader = $state();
let useTransitions = $state(false);
const ctaIsVisible = $derived(isSticky || !onlyShowCtaWhenSticky);
// Determine whether or not to render CTA
const renderCta = $derived(shouldRenderCta({
    primaryCtaButton,
    showCtaOnCtaPage,
    currentUrl: $currentUrlStore,
    loggedIn: $loggedInStore,
    cartCount: $cartCountStore,
}));
// Filtered CTA item
const filteredPrimaryCta = $derived(renderCta ? primaryCtaButton : undefined);
// Determine whether or not to render Secondary CTA
const renderSecondaryCta = $derived(shouldRenderSecondaryCta({
    secondaryCtaButton,
    loggedIn: $loggedInStore,
    cartCount: $cartCountStore,
}));
// Filtered Secondary CTA item
const filteredSecondaryCta = $derived(renderSecondaryCta ? secondaryCtaButton : undefined);
// Filtered nav items
const filteredNavItems = $derived(filterNavItems({
    items: navItems,
    loggedIn: $loggedInStore,
    cartCount: $cartCountStore,
}));
const activeLink = $derived(filteredNavItems.find((item) => urlsAreEqual(item.props?.url, $currentUrlStore))?.contentfulEntryId);
const reverseCtaButtons = $derived(filteredPrimaryCta && filteredSecondaryCta && reverseDesktopCtaButtons);
$effect(() => {
    setCtaSubnavButtonsVisibility({
        renderCta,
        renderSecondaryCta,
        ctaIsVisible,
        isSticky,
        hiddenUntilScrolled,
    });
});
onMount(() => {
    // Resetting the position styling fixes the sticky nav not 'sticking' caused by deferred JS
    // Context: https://jira.sqprod.co/browse/WEBENG-4190
    if (ctaSubnavDesktopHeader) {
        ctaSubnavDesktopHeader.style.position = '';
    }
    // Hack to avoid transitioning CTA immediately on mount
    setTimeout(() => {
        useTransitions = true;
    }, 100);
});
</script>

<div
  class="shim"
  aria-hidden="true"
  style:height={isSticky && !hiddenUntilScrolled ? `${desktopNavHeight}px` : '0px'}
></div>

<nav class="DesktopNav" bind:offsetWidth={desktopNavWidth} data-cta-subnav-desktop-v2>
  <div
    class="cta-subnav-container"
    class:isSticky
    class:useTransitions
    class:hiddenUntilScrolled
    data-entry-id={id}
    style:width={`${desktopNavWidth}px`}
    bind:offsetHeight={desktopNavHeight}
    bind:this={ctaSubnavDesktopHeader}
  >
    <div class="left-slot">
      {#if title}<span class="title">{title}</span>{/if}
    </div>

    <div class="right-slot">
      {#if filteredPrimaryCta || filteredSecondaryCta || filteredNavItems.length > 0}
        <div class="right-slot-content">
          <ul class="cta-subnav-list" class:ctaIsVisible class:primaryCtaFirst={reverseCtaButtons}>
            {#each filteredNavItems as navItem}
              {#if getComponentType(navItem.componentName) === 'category'}
                <li
                  class="flyout-toggle-item"
                  class:inactive={activeLink && activeLink !== navItem.contentfulEntryId}
                  data-category={navItem.props.title}
                >
                  <FlyoutToggle
                    id={navItem.contentfulEntryId}
                    title={navItem.props.title}
                    enableChevron={true}
                    alignment="right"
                  >
                    <LinkList
                      id={navItem.contentfulEntryId}
                      title={navItem.props.title}
                      excludeTitleOnDesktop={true}
                      navItems={navItem.props.primaryNavItems}
                    />
                  </FlyoutToggle>
                </li>
              {:else if getComponentType(navItem.componentName) === 'link'}
                <li class:inactive={activeLink && activeLink !== navItem.contentfulEntryId}>
                  <Link
                    linkCustomCssClass="nav-list-link"
                    id={navItem.contentfulEntryId}
                    title={navItem.props.title}
                    url={navItem.props.url}
                    linkStyle={navItem.props.linkStyle}
                    enableHoverOpacity={true}
                    openInNewTab={navItem.props.openInNewTab}
                  />
                </li>
              {/if}
            {/each}
          </ul>

          <ul class="cta-button-container">
            {#if filteredPrimaryCta && ctaIsVisible}
              <li class="cta-button" class:isVisible={ctaIsVisible}>
                <Link
                  id={filteredPrimaryCta?.contentfulEntryId}
                  title={filteredPrimaryCta?.props?.title}
                  url={filteredPrimaryCta?.props?.url}
                  linkStyle={filteredPrimaryCta?.props?.linkStyle}
                  openInNewTab={filteredPrimaryCta?.props?.openInNewTab}
                />
              </li>
            {/if}

            {#if filteredSecondaryCta}
              <li class="cta-button isVisible">
                <Link
                  id={filteredSecondaryCta?.contentfulEntryId}
                  title={filteredSecondaryCta?.props?.title}
                  url={filteredSecondaryCta?.props?.url}
                  linkStyle={filteredSecondaryCta?.props?.linkStyle}
                  openInNewTab={filteredSecondaryCta?.props?.openInNewTab}
                />
              </li>
            {/if}
          </ul>
        </div>
      {/if}
    </div>
  </div>
</nav>

<style>@keyframes fade-in-slide-from-top {
  from {
    opacity: 0;
    transform: translateX(0) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes fade-in-slide-from-left {
  from {
    opacity: 0;
    transform: translateX(-4px) translateY(0);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes slide-down {
  from {
    transform: translateY(-50%);
  }
  to {
    transform: translateY(0);
  }
}
/**
We use rem units for media queries to promote accessible layouts. By utilizing rem units, the computed values in media queries are determined by the user's browser-defined font size rather than our global 'font-size: 62.5%' reset. This approach ensures that our design adapts to individual user settings, enhancing accessibility. 
*/
.shim {
  background-color: var(--color-background, var(--color-neutral-1000, #fff));
}

.DesktopNav {
  display: none;
}
@media (min-width: 80rem) {
  .DesktopNav {
    display: flex;
  }
}
.DesktopNav:has(.cta-subnav-container.hiddenUntilScrolled:not(.isSticky)) {
  padding-top: 0px;
  padding-bottom: 0px;
}

.cta-subnav-container {
  padding-left: 32px;
  padding-right: 32px;
  box-sizing: border-box;
  position: relative;
  min-width: 320px;
  height: 72px;
  z-index: 1;
  padding-top: 4px;
  padding-bottom: 4px;
  background-color: var(--color-background, var(--color-neutral-1000, #fff));
  color: var(--color-text, var(--color-neutral-000, #000000));
  display: flex;
  align-items: center;
  width: 100%;
}
@media (min-width: 105rem) {
  .cta-subnav-container {
    --nav-outer-gutter-base: calc(var(--space-10) / 2);
    --nav-outer-gutter: max(var(--nav-outer-gutter-base), var(--dynamic-gutter));
    padding-left: calc(32px + var(--nav-outer-gutter)) !important;
    padding-right: calc(32px + var(--nav-outer-gutter)) !important;
  }
}
.cta-subnav-container .title {
  --letter-spacing: var(--text-variant-100-letter-spacing);
  --line-height: var(--text-variant-100-line-height);
  --font-size: var(--text-variant-100-font-size);
  --font-weight: var(--text-variant-100-font-weight);
  --font-family: var(--text-font-family);
  --max-width: var(--text-variant-100-max-width);
  color: var(--color-text);
  font-family: var(--font-family);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  letter-spacing: var(--letter-spacing);
  line-height: var(--line-height);
  max-width: var(--max-width);
  display: inline-block;
  padding: 10px 0;
  color: var(--color-text, var(--color-neutral-000, #000000));
}
.cta-subnav-container .right-slot-content {
  display: flex;
  align-items: center;
}
.cta-subnav-container .cta-subnav-list {
  display: block;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  list-style-type: none;
  display: flex;
  align-items: center;
}
.cta-subnav-container .cta-subnav-list > li {
  display: block;
  position: static;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}
.cta-subnav-container .cta-subnav-list > li::before {
  display: none;
}
.cta-subnav-container .cta-subnav-list > li :global(:last-child) {
  margin: 0;
}
.cta-subnav-container .cta-subnav-list li {
  transition: opacity 250ms;
}
.cta-subnav-container .cta-subnav-list li.inactive :global(.nav-list-link),
.cta-subnav-container .cta-subnav-list li.inactive :global(button) {
  opacity: 43%;
}
.cta-subnav-container .cta-subnav-list .flyout-toggle-item :global(.isOpen button) {
  opacity: 100%;
}
.cta-subnav-container .cta-button-container {
  display: block;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  list-style-type: none;
  display: flex;
  flex-direction: row;
  gap: 20px;
  margin-left: 10px;
}
.cta-subnav-container .cta-button-container > li {
  display: block;
  position: static;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}
.cta-subnav-container .cta-button-container > li::before {
  display: none;
}
.cta-subnav-container .cta-button-container > li :global(:last-child) {
  margin: 0;
}
.cta-subnav-container .cta-button-container .cta-button {
  opacity: 0;
}
.cta-subnav-container .cta-button-container .cta-button.isVisible {
  opacity: 1;
}
.cta-subnav-container .primaryCtaFirst :global(li.cta-button) {
  order: 1;
}
.cta-subnav-container .primaryCtaFirst :global(li.cta-button) :global(+ li.cta-button) {
  order: 0;
}
.cta-subnav-container .left-slot {
  flex: 1 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cta-subnav-container .right-slot {
  flex: 0 0 auto;
  justify-content: flex-end;
}

.hiddenUntilScrolled {
  position: fixed;
  z-index: 2;
  top: 0;
  transition: transform 200ms cubic-bezier(0.4, 0, 1, 1);
  transform: translateY(-100%);
}

.isSticky {
  position: fixed;
  z-index: 2;
  top: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
  width: 100%;
}
.isSticky.hiddenUntilScrolled {
  transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
  transform: translateY(0);
}

.useTransitions .cta-subnav-list {
  transition: transform 200ms cubic-bezier(0.4, 0, 0.2, 1);
}
.useTransitions .cta-subnav-list.ctaIsVisible {
  transition: transform 250ms cubic-bezier(0.4, 0, 0.2, 1);
}</style>
